import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Clipboard, Check, FileText, ArrowDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { cn } from '@/lib/utils';
import { useChatSSE } from '@/modules/gpt-chats/hooks/use-chat-sse';
import { useChatStore } from '@/modules/gpt-chats/hooks/use-chat-store';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { ChatFileMetadata } from '@/modules/gpt-chats/types/chat-store.types';
import { useGetLlmModels } from '@/modules/gpt-chats/hooks/use-gpt-chat';
import { PreviewChatInput as ChatInput } from '@/modules/gpt-chats/components/agents-configuration/components/preview-section/preview-chatbot/preview-chat-input';
import { ChatEventMessage, SparkleText } from '@/modules/gpt-chats/utils/chat-event-messages';
import { handleCopyRaw, handleCopyWithStyling } from '@/modules/gpt-chats/utils/copy-chats';
import { MarkdownRenderer } from '@/modules/gpt-chats/components/markdown-renderer/markdown-renderer';
import { PreviewChatItemSuggestions } from '@/modules/gpt-chats/components/agents-configuration/components/preview-section/preview-chatbot/preview-chat-item-suggestions';
import { ChatbotWidget } from './index.type';
import { ChatHeader } from './chat-header';
import DummyProfile from '@/assets/images/dummy_profile.png';

const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const ThinkingIndicator = ({ logoUrl }: { logoUrl: string }) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-3 items-start ml-1 min-h-[40px] duration-500 ease-in-out animate-in fade-in slide-in-from-bottom-2">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
        <img src={logoUrl} alt="bot" className="h-full w-full object-contain" />
      </div>
      <div className="flex-1 py-1">
        <div className="flex items-center gap-2">
          <SparkleText text={t('SENDING')} />
        </div>
      </div>
    </div>
  );
};

const ChatEventMessageIndicator = ({ message, logoUrl }: { message: string; logoUrl: string }) => (
  <div className="flex gap-3 items-start ml-1 min-h-[40px] duration-500 ease-in-out animate-in fade-in slide-in-from-bottom-2">
    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
      <img src={logoUrl} alt="bot" className="h-full w-full object-contain" />
    </div>
    <div className="flex-1 py-1">
      <ChatEventMessage message={message} />
    </div>
  </div>
);

type ChatbotProps = {
  widgetId: string;
  agentId?: string;
  widget: ChatbotWidget;
  // humanLogoUrl: string;
};

export const Chatbot = ({ widgetId, agentId, widget }: ChatbotProps) => {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToBottomRef = useRef<boolean>(false);
  const userHasManuallyScrolledRef = useRef<boolean>(false);
  const { data: accountData } = useGetAccount();
  const { data: llmModels } = useGetLlmModels();

  const {
    conversations,
    isBotStreaming,
    isBotThinking,
    isReady,
    selectedModel,
    currentEvent,
    generateBotMessage,
  } = useChatSSE({
    chatId: widgetId,
    agentId: agentId,
    widgetId: widgetId,
    isPreview: true,
  });

  useEffect(() => {
    hasScrolledToBottomRef.current = false;
    userHasManuallyScrolledRef.current = false;
    setSuggestions([]);
  }, [widgetId]);

  // Detect user manual scrolling with wheel event
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      if (container.scrollHeight > container.clientHeight) {
        const distanceFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight;

        if (e.deltaY > 0 && distanceFromBottom < 10) return;

        if (e.deltaY !== 0) {
          userHasManuallyScrolledRef.current = true;
        }
        return;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Detect scroll position to show/hide scroll-to-bottom button
  useEffect(() => {
    const container = scrollContainerRef.current;

    const checkScroll = () => {
      if (container && container.scrollHeight > container.clientHeight) {
        const { scrollHeight, scrollTop, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const shouldShow = distanceFromBottom > 50;
        setShowScrollButton(shouldShow);
        return;
      }
    };

    const handleScroll = () => {
      requestAnimationFrame(checkScroll);
    };

    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    const initialTimeout = setTimeout(checkScroll, 300);
    window.addEventListener('resize', handleScroll);

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', handleScroll);
      clearTimeout(initialTimeout);
    };
  }, [conversations.length]);

  // Auto-scroll effect
  useEffect(() => {
    if (!isReady || conversations.length === 0) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    if (userHasManuallyScrolledRef.current && isBotStreaming) {
      return;
    }

    let distanceFromBottom = 0;
    if (container.scrollHeight > container.clientHeight) {
      distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    }

    if (distanceFromBottom < 20) {
      userHasManuallyScrolledRef.current = false;
    }

    const shouldSmoothScroll = distanceFromBottom > 100;
    const behavior = isBotStreaming
      ? shouldSmoothScroll
        ? 'smooth'
        : 'auto'
      : hasScrolledToBottomRef.current
        ? 'smooth'
        : 'auto';
    const delay = isBotStreaming ? 10 : hasScrolledToBottomRef.current ? 100 : 0;

    const timeoutId = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: behavior });
      hasScrolledToBottomRef.current = true;
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [conversations, isBotStreaming, isReady]);

  const handleSendMessage = async (message: string, files?: ChatFileMetadata[]) => {
    if (!message.trim()) return;
    userHasManuallyScrolledRef.current = false;
    setSuggestions([]);

    const activeChatId = useChatStore.getState().resolveChatId(widgetId);
    useChatStore.getState().addUserMessage(activeChatId, message, files);

    await generateBotMessage({
      message,
      files,
      setSuggestions,
    });
  };

  const scrollToBottom = () => {
    userHasManuallyScrolledRef.current = false;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getModelLabel = (modelName?: string) => {
    if (!modelName) return null;
    const model = llmModels?.find((m) => m.model_name === modelName);
    return model?.model_name_label || modelName;
  };

  const wigetImageUrl = widget?.logo_url || `/assets/images/agents/widget.png`;
  // const humanImageUrl = humanLogoUrl || `/assets/images/dummy_profile.png`;

  return (
    <div
      id={`chatbot-container-${widgetId}`}
      className="flex h-full w-full flex-col items-start overflow-hidden rounded-lg border shadow-lg relative bg-background"
      style={{
        background: widget.designSettings.chatBackgroundColor,
        borderColor: widget.designSettings?.borderColor,
      }}
    >
      <ChatHeader name={widget.name} config={widget.designSettings} logoUrl={wigetImageUrl} />

      <div
        ref={scrollContainerRef}
        className="flex w-full flex-1 flex-col gap-6 overflow-y-auto border-l border-r px-6 py-4 pb-6 text-sm font-normal"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          color: widget.designSettings.chatFontColor,
          borderColor: widget.designSettings?.borderColor,
        }}
      >
        {isReady && widget?.greeting && (
          <div className="flex gap-3 justify-start items-start animate-in fade-in duration-500 ease-in-out">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
              <img src={wigetImageUrl} alt="bot" className="h-full w-full object-contain" />
            </div>
            <div className="group flex-1 min-w-0 relative flex flex-col gap-1">
              <div className="min-w-0 py-1" style={{ color: widget.designSettings.chatFontColor }}>
                <MarkdownRenderer
                  content={widget.greeting}
                  className={`text-[${widget.designSettings.chatFontColor}]`}
                />
              </div>
            </div>
          </div>
        )}

        {isReady &&
          conversations.map((msg, index) => (
            <div
              key={index}
              className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'} ${msg.type === 'bot' ? 'items-start' : ''} ${msg.type === 'bot' && !msg.streaming ? 'animate-in fade-in duration-500 ease-in-out' : ''}`}
            >
              {msg.type === 'bot' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                  <img src={wigetImageUrl} alt="bot" className="h-full w-full object-contain" />
                </div>
              )}

              <div
                className={`group flex-1 min-w-0 relative ${msg.type === 'user' ? 'flex flex-col items-end gap-1.5' : 'flex flex-col gap-1'}`}
              >
                {msg.type === 'bot' && (msg.tokenUsage?.model_name || selectedModel?.model) && (
                  <span className="text-muted-foreground text-xs font-medium">
                    {widget.name ||
                      getModelLabel(msg.tokenUsage?.model_name || selectedModel?.model)}
                  </span>
                )}

                <div
                  className={`${msg.type === 'bot' ? 'min-w-0' : 'max-w-[85%] min-w-0'} py-1.5 px-4 rounded-xl ${msg.type === 'user' ? 'bg-primary text-primary-foreground' : ''}`}
                  style={
                    msg.type === 'bot'
                      ? { color: widget.designSettings.chatFontColor }
                      : {
                          backgroundColor: widget.designSettings.sendButtonBackgroundColor,
                          color: widget.designSettings.sendButtonFontColor,
                        }
                  }
                >
                  {msg.type === 'user' ? (
                    <p className="text-sm leading-6 whitespace-pre-wrap">{msg.message}</p>
                  ) : (
                    <MarkdownRenderer
                      content={msg.message}
                      isStreaming={msg.streaming && isBotStreaming}
                      className={`text-[${widget.designSettings.chatFontColor}]`}
                    />
                  )}
                </div>

                {!msg.streaming && (
                  <div
                    className={cn(
                      'absolute -bottom-6 flex items-center gap-1.5',
                      msg.type === 'user' ? 'right-0' : 'left-0'
                    )}
                  >
                    {msg.timestamp && (
                      <span className="text-[10px] text-gray-400 cursor-default px-1">
                        {formatTimestamp(msg.timestamp)}
                      </span>
                    )}
                    {msg.type === 'bot' ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 rounded-lg hover:bg-muted"
                          >
                            {copiedId === index ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Clipboard className="h-3 w-3" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          <DropdownMenuItem
                            onClick={() =>
                              handleCopyWithStyling(index, { setCopiedId, conversations })
                            }
                          >
                            <Clipboard className="h-4 w-4 mr-2" />
                            {t('COPY_WITH_STYLING')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleCopyRaw(msg.message, index, { setCopiedId })}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            {t('COPY_RAW_RESPONSE')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 rounded-lg hover:bg-muted"
                        onClick={() => handleCopyRaw(msg.message, index, { setCopiedId })}
                      >
                        {copiedId === index ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Clipboard className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {msg.type === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center overflow-hidden">
                  <img
                    src={
                      accountData?.profileImageUrl && accountData.profileImageUrl !== ''
                        ? accountData.profileImageUrl
                        : DummyProfile
                    }
                    alt="profile"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          ))}

        {isBotThinking && (
          <div key="thinking-indicator" className="animate-in fade-in duration-500 ease-in-out">
            {currentEvent ? (
              <ChatEventMessageIndicator message={currentEvent.message} logoUrl={wigetImageUrl} />
            ) : (
              <ThinkingIndicator logoUrl={wigetImageUrl} />
            )}
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="pt-2">
            <PreviewChatItemSuggestions
              suggestions={suggestions}
              onSelect={(suggestion) => handleSendMessage(suggestion)}
            />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {showScrollButton && (
        <div className="absolute bottom-[80px] left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full shadow-xl bg-background hover:bg-accent border border-border backdrop-blur-sm"
            onClick={scrollToBottom}
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <ChatInput
        config={widget.designSettings}
        onSubmit={handleSendMessage}
        disable={isBotStreaming || isBotThinking}
      />
    </div>
  );
};
